import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Grid, FormControl, Box, Checkbox, FormControlLabel, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AlertControl from "../../../components/Controls/AlertControl";
import AlertWithTitle from '../../../components/Controls/AlertWithTitle';
import LoadingOverlay from 'react-loading-overlay';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { APICall, WriteToActivityLog } from '../../../global/Api';
import { ActivityLogEvent } from '../../../common/AOBEnum';
import { ErrorMessages } from '../../../common/ErrorMessages';
import dayjs from 'dayjs';
import { IsError } from '../../../global/APIUtils';
import { goToTop } from '../../../global/Helpers';

const CreateUserDlg = (props) => {
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState(null);
    const [successMsgTitle, setSuccessMsgTitle] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [titleData, setTitleData] = useState(null);
    const [roleData, setRoleData] = useState([]);
    const [pwdLenMin] = useState(10);
    const [showStrength, setShowStrength] = useState("none");
    const [strength, setStrength] = useState("");
    const [strengthColor, setStrengthColor] = useState("");

    const pwdRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;
    const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
    const today = dayjs();

    const validationSchema = yup.object({
        Password: yup.string()
            .max(20, "Should have less than or equal to 20 characters")
            .min(pwdLenMin, "Should have more than or equal to " + pwdLenMin + " characters")
            .matches(pwdRegExp, "Should have one uppercase, one lowercase, one number and one special character")
            .required('Password - required.'),
        RenteredPassword: yup.string()
            .max(20, "Should have less than or equal to 20 characters")
            .min(pwdLenMin, "Should have more than or equal to " + pwdLenMin + " characters")
            .matches(pwdRegExp, "Should have one uppercase, one lowercase, one number and one special character")
            .when("Password", {
                is: psswrd => (psswrd && psswrd.length > 0 ? true : false),
                then: yup.string().oneOf([yup.ref("Password")], "Password and confirm password does not match")
            })
            .required('Confirm Password - required.'),
        FirstName: yup.string()
            .max(40, "First Name - must be a string with a maximum length of '40'.")
            .trim('First Name - cannot contain leading and trailing spaces')
            .required('First Name - required.'),
        LastName: yup.string()
            .max(40, "Last Name - must be a string with a maximum length of '40'.")
            .trim('Last Name - cannot contain leading and trailing spaces')
            .required('Last Name - required.'),
        email: yup.string()
            .max(80, "Email Address - must be a string with a maximum length of 80")
            .email('Email Address - Invalid email address')
            .required('Email Address - required'),
        Mobile: yup.string()
            .required('Mobile Number - required.'),
        // userrole: yup.number()
        //     .required("Role - required.")
        //     .min(1, "Role - required."),
        Title: yup.number(),
        Gender: yup.number(),
        DOB: yup.date().nullable()
    });

    const loadFormData = async () => {
        try {
            setIsLoading(true);

            let apiUrl;
            let response;

            // Get titles
            apiUrl = new URL(
                process.env.REACT_APP_USER_TITLES,
                process.env.REACT_APP_BASE_URL,
            );

            response = await APICall({
                url: apiUrl.href,
                method: 'GET',
                contentType: 'application/json',
                responseType: ''
            }, props.userId, props.sessionId); // Use props.userId and props.sessionId

            if (response !== undefined && response !== null) {
                setTitleData(response["data"]);
            }

            // Get roles
            apiUrl = new URL(
                process.env.REACT_APP_GET_ALL_USERROLES,
                process.env.REACT_APP_BASE_URL,
            );

            response = await APICall({
                url: apiUrl.href,
                method: 'GET',
                contentType: 'application/json',
                responseType: ''
            }, props.userId, props.sessionId); // Use props.userId and props.sessionId

            if (response !== undefined && response !== null) {
                setRoleData(response["data"].filter(role => role.RoleTypeId === 2)); // Include only Guest User Role
            }
        } catch (err) {
            setErrorMsg(ErrorMessages.UnknownError);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadFormData();
    }, []);

    function evaluatePasswordStrength(password) {
        let score = 0;

        if (!password) {
            setShowStrength('none');
            return '';
        }

        // Check password length
        if (password.length > 11) score += 1;
        // Contains lowercase
        if (/[a-z]/.test(password)) score += 1;
        // Contains uppercase
        if (/[A-Z]/.test(password)) score += 1;
        // Contains numbers
        if (/\d/.test(password)) score += 1;
        // Contains special characters
        if (/[^A-Za-z0-9]/.test(password)) score += 1;

        setShowStrength('inline-block');
        switch (score) {
            case 0:
            case 1:
            case 2:
                setStrengthColor('#f00');
                return "Weak";
            case 3:
            case 4:
                setStrengthColor('#ffa500');
                return "Medium";
            case 5:
                setStrengthColor('#228b22');
                return "Strong";
        }
    }

    const userForm = useFormik({
        initialValues: {
            Password: '',
            RenteredPassword: '',
            Title: -1,
            FirstName: '',
            LastName: '',
            email: '',
            Phone: '',
            Mobile: '',
            Gender: -1,
            DOB: null,
            userrole: 2,
            ChkChngPwdNxtLogin: true
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setIsLoading(true);
                setErrorMsg('');

                const apiUrl = new URL(
                    process.env.REACT_APP_CREATE_USER_PROFILE,
                    process.env.REACT_APP_BASE_URL
                );

                // Format DOB as MM/DD/YYYY if present
                let birthDate = values.DOB ? dayjs(values.DOB) : null;
                let formattedDOB = birthDate ? birthDate.format('MM/DD/YYYY') : null;

                const inputJson = {
                    UserId: -1,
                    Title: parseInt(values.Title),
                    FirstName: values.FirstName,
                    LastName: values.LastName,
                    DOB: formattedDOB,
                    GenderId: parseInt(values.Gender),
                    EmailAddress: values.email,
                    PhoneNbr: values.Phone,
                    MobileNbr: values.Mobile,
                    RoleTypeId: parseInt(values.userrole),
                    Password: values.Password,
                    CPassword: values.RenteredPassword,
                    PwdChangeAtLogon: values.ChkChngPwdNxtLogin
                };

                let response = await APICall(
                    { url: apiUrl.href, method: 'POST', payload: inputJson, contentType: 'application/json' },
                    props.userId,
                    props.sessionId
                );

                if (response !== null && response !== undefined) {

                    setIsLoading(false);

                    let error = IsError(response);
                    if (error != 0) {
                        setErrorMsg(response.Errors[0].Message);
                        goToTop();
                        return;
                    }
                    if (response.meta.UserUpdated == "Yes") {
                        await WriteToActivityLog(ActivityLogEvent.UserManagement, "User created. User: '" + values.email + "'", props.userId, props.sessionId);
                        setSuccessMsg("User created successfully");
                        setSuccessMsgTitle("Success");
                        setTimeout(() => {
                            props.onClose(true);
                        }, 1000);
                    } else {
                        setErrorMsg(ErrorMessages.UnknownError);
                    }
                }

                // Check for API errors
                // if (response && typeof IsError === 'function' && IsError(response) !== 0) {
                //     setErrorMsg(response.Errors && response.Errors[0] ? response.Errors[0].Message : ErrorMessages.UnknownError);
                //     setIsLoading(false);
                //     return;
                // }

                // if (response && response.data) {
                //     await WriteToActivityLog(ActivityLogEvent.UserManagement, "New user created.", props.userId, props.sessionId);
                //     setSuccessMsg("User created successfully");
                //     setSuccessMsgTitle("Success");
                //     setTimeout(() => {
                //         props.onClose(true);
                //     }, 2000);
                // } else {
                //     setErrorMsg(ErrorMessages.UnknownError);
                // }
            } catch (err) {
                setErrorMsg(ErrorMessages.UnknownError);
            } finally {
                setIsLoading(false);
            }
        }
    });

    return (
        <Dialog
            open={props.open}
            maxWidth="md"
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "700px",
                    },
                },
            }}
        >
            <LoadingOverlay active={isLoading} spinner>
                <DialogTitle>
                    <Box sx={{ pl: 1.5, pt: 1 }}>
                        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 500 }}>
                            Create New User
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => props.onClose(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 12,
                            color: (theme) => theme.palette.Icon.main,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={userForm.handleSubmit} autoComplete="off">
                    <DialogContent dividers>
                        <Box sx={{ pl: 4, pr: 4, pb: 1 }}>
                            <AlertControl Message={errorMsg} severity="error" color="error" icon={true} />
                            <AlertWithTitle
                                Message={successMsg}
                                variant="body1"
                                severity="success"
                                color="success"
                                icon={true}
                                Title={successMsgTitle}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="Title" className="form-label">Title</label>
                                        <select
                                            id="Title"
                                            name="Title"
                                            className="form-select"
                                            value={userForm.values.Title}
                                            onChange={userForm.handleChange}
                                        >
                                            <option value={-1}>Select</option>
                                            {titleData && titleData.map((title) => (
                                                <option key={title.TitleId} value={title.TitleId}>
                                                    {title.TitleName}
                                                </option>
                                            ))}
                                        </select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="Gender" className="form-label">Gender</label>
                                        <select
                                            id="Gender"
                                            name="Gender"
                                            className="form-select"
                                            value={userForm.values.Gender}
                                            onChange={userForm.handleChange}
                                        >
                                            <option key={-1} value={-1}>Select</option>
                                            <option value="70">Male</option>
                                            <option value="71">Female</option>
                                            <option value="74">Other</option>
                                        </select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="FirstName" className="form-label">First Name*</label>
                                        <input
                                            type="text"
                                            id="FirstName"
                                            name="FirstName"
                                            className="form-control"
                                            value={userForm.values.FirstName}
                                            onChange={userForm.handleChange}
                                        />
                                        {userForm.touched.FirstName && userForm.errors.FirstName && (
                                            <p className="text-danger">{userForm.errors.FirstName}</p>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="LastName" className="form-label">Last Name*</label>
                                        <input
                                            type="text"
                                            id="LastName"
                                            name="LastName"
                                            className="form-control"
                                            value={userForm.values.LastName}
                                            onChange={userForm.handleChange}
                                        />
                                        {userForm.touched.LastName && userForm.errors.LastName && (
                                            <p className="text-danger">{userForm.errors.LastName}</p>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <label htmlFor="email" className="form-label">Email*</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            className="form-control"
                                            value={userForm.values.email}
                                            onChange={userForm.handleChange}
                                            autoComplete="off"
                                            autoCorrect="off"
                                            spellCheck="false"
                                        />
                                        {userForm.touched.email && userForm.errors.email && (
                                            <p className="text-danger">{userForm.errors.email}</p>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="Mobile" className="form-label">Mobile Number*</label>
                                        <input
                                            type="tel"
                                            id="Mobile"
                                            name="Mobile"
                                            className="form-control"
                                            value={userForm.values.Mobile}
                                            onChange={userForm.handleChange}
                                        />
                                        {userForm.touched.Mobile && userForm.errors.Mobile && (
                                            <p className="text-danger">{userForm.errors.Mobile}</p>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="Phone" className="form-label">Phone Number</label>
                                        <input
                                            type="tel"
                                            id="Phone"
                                            name="Phone"
                                            className="form-control"
                                            value={userForm.values.Phone}
                                            onChange={userForm.handleChange}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="Password" className="form-label">Password*</label>
                                        <input
                                            type="password"
                                            id="Password"
                                            name="Password"
                                            className="form-control"
                                            value={userForm.values.Password}
                                            onChange={(e) => {
                                                userForm.handleChange(e);
                                                setStrength(evaluatePasswordStrength(e.target.value));
                                            }}
                                            autoComplete="new-password"
                                            autoCorrect="off"
                                            spellCheck="false"
                                        />
                                        {userForm.touched.Password && userForm.errors.Password && (
                                            <p className="text-danger">{userForm.errors.Password}</p>
                                        )}
                                        <Stack direction="row" spacing={1}>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: showStrength, pt: 1, pb: 0, fontWeight: 500, }} >Password strength: </Typography>
                                            <Typography variant="body2" color={strengthColor} sx={{ pt: 1, pb: 0, fontWeight: 700, }} >{strength}</Typography>
                                        </Stack>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="RenteredPassword" className="form-label">Confirm Password*</label>
                                        <input
                                            type="password"
                                            id="RenteredPassword"
                                            name="RenteredPassword"
                                            className="form-control"
                                            value={userForm.values.RenteredPassword}
                                            onChange={userForm.handleChange}
                                            autoComplete="new-password"
                                            autoCorrect="off"
                                            spellCheck="false"
                                        />
                                        {userForm.touched.RenteredPassword && userForm.errors.RenteredPassword && (
                                            <p className="text-danger">{userForm.errors.RenteredPassword}</p>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="userrole" className="form-label">Role*</label>
                                        <select
                                            id="userrole"
                                            name="userrole"
                                            className="form-select"
                                            // value={userForm.values.userrole}
                                            value={roleData[0] ? roleData[0].RoleTypeId : -1}
                                            // selected={roleData[0] ? roleData[0].RoleTypeId : -1}
                                            onChange={userForm.handleChange}
                                            disabled={true}
                                        >
                                            <option value={-1}>Select</option>
                                            {roleData && roleData.map((role) => (
                                                <option key={role.RoleTypeId} value={role.RoleTypeId}>
                                                    {role.RoleName}
                                                </option>
                                            ))}
                                        </select>
                                        {userForm.touched.userrole && userForm.errors.userrole && (
                                            <p className="text-danger">{userForm.errors.userrole}</p>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <label htmlFor="DOB" className="form-label">Date of Birth</label>
                                        <input
                                            type="date"
                                            id="DOB"
                                            name="DOB"
                                            className="form-control"
                                            value={userForm.values.DOB || ''}
                                            onChange={userForm.handleChange}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={userForm.values.ChkChngPwdNxtLogin}
                                                onChange={userForm.handleChange}
                                                name="ChkChngPwdNxtLogin"
                                                color="primary"
                                            />
                                        }
                                        label="User must change password at next logon"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ mr: 3.5, pb: 1.5 }}>
                        <Button
                            onClick={() => props.onClose(false)}
                            variant="contained"
                            sx={{ mt: 1, mb: 1, borderRadius: '20px', padding: '0.25rem 2rem', mr: 1.5, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ mt: 1, mb: 1, borderRadius: '20px', padding: '0.25rem 2rem', textTransform: 'none' }}
                        >
                            Create User
                        </Button>
                    </DialogActions>
                </form>
            </LoadingOverlay>
        </Dialog>
    );
};

export default CreateUserDlg;